import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Spinner from 'react-bootstrap/Spinner';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useStorage } from '../../hooks/use-storage';
import { layoutDefaults } from '../../utils/layout-defaults';
import {
  parseContainerBackgroundShade,
  parseDefaultTextColor,
  parseLayoutBoxShadow,
  parseSelectedColor,
} from '../../utils/manager/layout-manager';
import {
  isCancellable as isCancellableOrder,
  isCancelled as isCancelledOrder,
  isPayable,
  isSelectable as isSelectableOrder,
} from '../../utils/manager/order-manager';
import { parsePaymentStatus } from '../../utils/manager/payment-manager';
import { parseValue } from '../../utils/manager/price-manager';
import { isExpired } from '../../utils/manager/session-manager';
import { DeliveredSequence } from '../../utils/manager/step-manager';
import { TimeFrom } from '../time-from';
import { OrderDescription } from './order-description';
import { Step } from './step';

const animation = ['animate__animated', 'animate__pulse', 'animate__faster'];

function Order({
  index,
  count,
  order,
  refreshTime,
  selectedOrders,
  onSelectedOrdersChange: setSelectedOrders,
  cancelDisabled,
  onCancel,
  prepaid,
}) {
  const { menuLayout, user, spot } = useStorage();
  const [loading, setLoading] = useState(false);
  // const [play] = useSound(selectSfx, { volume: layoutDefaults.lowerSoundVolume })
  const ref = useRef();
  const { t } = useTranslation();

  const isSelfOrder = useMemo(
    () => order.bill.customer.id === user?.id,
    [order.bill.customer.id, user?.id]
  );

  const isSelectable = useMemo(() => isSelectableOrder(order), [order]);
  const isCancelled = useMemo(() => isCancelledOrder(order), [order]);
  const isLowOpacity = useMemo(
    () =>
      !isSelectable &&
      (order?.progress?.step?.sequence === DeliveredSequence ||
        isCancelledOrder(order)),
    [isSelectable, order]
  );

  function highlight(i = -1) {
    const delay = i < 0 ? '0' : '1';

    ref.current.style.setProperty('--animate-delay', `.${count - i}s`);
    ref.current.classList.add(...animation, `animate__delay-${delay}s`);

    ref.current.addEventListener('animationend', () => {
      ref.current.classList.remove(...animation, `animate__delay-${delay}s`);
      ref.current.style.removeProperty('--animate-delay');
    });
  }

  useEffect(() => {
    if (order.flashAt != null && index != null) {
      highlight(index);
    }
  }, [order.flashAt, index]);

  const unselect = useCallback(
    () => setSelectedOrders(selectedOrders.filter((o) => o.id !== order.id)),
    [order.id, selectedOrders, setSelectedOrders]
  );

  const isSelected = useMemo(
    () => selectedOrders?.find((o) => o.id === order.id) != null,
    [order.id, selectedOrders]
  );

  const isCancellable = useMemo(
    () => isCancellableOrder(order, user),
    [order, user]
  );

  const parsedStatus = useMemo(
    () => parsePaymentStatus(order.payment?.status),
    [order.payment?.status]
  );

  const classes = useStyles({
    layout: menuLayout,
    isCancellable,
    isSelected,
    isCancelled,
    isSelfOrder,
    isSelectable,
    isLowOpacity,
    order,
    parsedStatus,
  });

  useEffect(() => {
    if (!isSelectable && isSelected) {
      unselect();
    }
  }, [isSelected, isSelectable, unselect]);

  function toogleSelected() {
    if (isSelected) {
      unselect();
    } else if (isSelectable) {
      highlight();
      // playSound();
      setSelectedOrders([...selectedOrders, order]);
    }
  }

  const parsedName = useMemo(() => {
    if (isSelfOrder) {
      return t('you');
    }
    return order.bill.customer.name;
  }, [isSelfOrder, order.bill.customer.name]);

  const callback = useCallback(({ loading: _loading }) => {
    setLoading(_loading);
  }, []);

  async function handleCancel(event) {
    event.stopPropagation();

    if (loading || cancelDisabled) return;
    onCancel?.call(this, order, callback);
  }

  return (
    <div
      ref={ref}
      className={classes.container}
      onClick={toogleSelected}
      aria-hidden
    >
      <div className={classes.leftContainer}>
        <div className={classes.headerContainer}>
          <div className={classes.headerLeft}>
            <Step
              step={order.progress.step}
              cancelled={isCancelled}
              prepaid={spot?.prepaid}
              paid={order.payment?.status === 'paid'}
              isLowOpacity={isLowOpacity}
            />
          </div>
          <div className={classes.headerRight}>
            <span>
              <span className={classes.headerRightContent}>
                <span>{parsedName} </span>
                {!prepaid || isExpired(order.session)
                  ? t('bill.order.requested')
                  : t('bill.order.added')}
                {!isCancellable && (
                  <>
                    {' '}
                    <TimeFrom
                      time={order.session.expires_at ?? order.created_at}
                      from={refreshTime}
                    />
                  </>
                )}
              </span>
            </span>
          </div>
        </div>
        <OrderDescription
          className={classes.content}
          order={order}
          isSelfOrder={isSelfOrder}
        />
      </div>
      <div className={classes.rightContainer}>
        {parsedStatus != null ? (
          <div className={classes.status}>{parsedStatus}</div>
        ) : (
          isCancellable && (
            <div onClick={handleCancel} className={classes.cancel} aria-hidden>
              {loading && (
                <Spinner
                  animation="border"
                  size="sm"
                  className={classes.spinner}
                />
              )}
              {prepaid ? t('remove') : t('cancel')}
            </div>
          )
        )}
        <div className={classes.price}>{parseValue(order.value)}</div>
      </div>
    </div>
  );
}

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    fontSize: layoutDefaults.fontSize,
    color: ({ layout }) => parseDefaultTextColor(layout),
    backgroundColor: ({ isSelected, layout, isCancelled }) => {
      if (isSelected) {
        return parseSelectedColor(layout, 1);
      }
      if (isCancelled) {
        return parseSelectedColor(layout, 0.2);
      }
      return parseSelectedColor(layout, 0);
    },
    padding: '18px 14px 18px 14px',
    marginBottom: 10,
    cursor: ({ isSelectable }) => (isSelectable ? 'pointer' : ''),
    borderRadius: layoutDefaults.radius,
    border: ({ layout, isSelectable, isSelfOrder, isCancelled }) =>
      `${isSelfOrder && !isCancelled && isSelectable ? 1.5 : 0.5}px ${
        isSelectable ? 'solid' : 'dashed'
      } ${
        isSelfOrder
          ? layoutDefaults.buttonBackgroundPrimary
          : parseDefaultTextColor(layout, layoutDefaults.smallOpacity)
      }`,

    boxShadow: ({ layout, isSelectable }) =>
      parseLayoutBoxShadow(layout, !isSelectable),
  },
  leftContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',

    // border: '1px solid yellow',
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'flex-start',

    // border: '1px solid blue',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    marginRight: 10,
    opacity: ({ order, isLowOpacity }) =>
      !isPayable(order) && isLowOpacity && layoutDefaults.smallOpacity,
    // border: '1px solid pink',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    fontSize: layoutDefaults.smallFontSize,
    fontWeight: 'lighter',
  },
  headerRightContent: {
    opacity: ({ isLowOpacity }) =>
      !isLowOpacity ? layoutDefaults.opacity : layoutDefaults.smallOpacity,
  },
  content: {
    paddingTop: 12,
    textDecoration: ({ isCancelled }) => isCancelled && 'line-through',
    opacity: ({ isLowOpacity }) => isLowOpacity && layoutDefaults.smallOpacity,
  },
  rightContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: ({ parsedStatus, isCancellable }) =>
      parsedStatus == null && !isCancellable ? 'center' : 'space-between',
    paddingLeft: layoutDefaults.smallHorizontalMargin,
    // border: '1px solid pink',
  },
  selfName: {
    fontSize: layoutDefaults.bigFontSize,
    textTransform: 'uppercase',
    fontWeight: 'bold',
  },
  status: {
    fontSize: layoutDefaults.smallestFontSize,
    fontWeight: 'bold',
    textTransform: 'uppercase',

    padding: '3px 5px 2px 5px',
    backgroundColor: ({ layout }) => parseDefaultTextColor(layout),
    color: ({ layout }) => parseContainerBackgroundShade(layout),

    borderRadius: layoutDefaults.smallRadius,
  },
  price: {
    textAlign: 'right',
    textDecoration: ({ isCancelled }) => isCancelled && 'line-through',
    opacity: ({ isLowOpacity }) => isLowOpacity && layoutDefaults.smallOpacity,
    // border: '1px solid pink',
  },
  spinner: {
    height: layoutDefaults.bigFontSize,
    width: layoutDefaults.bigFontSize,
    marginRight: layoutDefaults.smallerHorizontalMargin,
  },
  cancel: {
    cursor: 'pointer',
    fontSize: layoutDefaults.fontSize,

    color: layoutDefaults.buttonText,
    backgroundColor: layoutDefaults.buttonBackgroundPrimary,

    paddingTop: layoutDefaults.smallVerticalMargin,
    paddingBottom: layoutDefaults.smallVerticalMargin,
    paddingLeft: layoutDefaults.horizontalMargin,
    paddingRight: layoutDefaults.horizontalMargin,

    boxShadow: ({ layout, isEmpty }) =>
      !isEmpty && `2px 2px 2px ${parseContainerBackgroundShade(layout, 5)}`,
    border: `1px solid ${layoutDefaults.buttonBorder}`,
    borderRadius: 3,
    marginBottom: layoutDefaults.smallVerticalMargin,
  },
});

export { Order };
